import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, Clock, CheckCircle2, Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { name: "", email: "", phone: "" };

export default function PersonnelApply() {
  const navigate = useNavigate();
  const { continueWithEmailPhone } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [channel, setChannel] = useState("email"); // "email" | "sms"
  const [step, setStep] = useState("form"); // form | code | done
  const [code, setCode] = useState("");
  const [masked, setMasked] = useState("");
  const [devCode, setDevCode] = useState("");
  const [busy, setBusy] = useState(false);

  const identifier = channel === "email" ? form.email.trim().toLowerCase() : form.phone.trim();

  const requestCode = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) { toast.error("Full name is required"); return; }
    if (!form.email.trim()) { toast.error("Email is required"); return; }
    if (!form.phone.trim()) { toast.error("Phone is required"); return; }
    if (channel === "sms" && !form.phone.trim().startsWith("+")) {
      toast.error("Phone must be E.164 format, e.g. +14155550100"); return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/otp/request`, { identifier, channel });
      setMasked(data.masked || "");
      if (data.dev_code) setDevCode(data.dev_code);
      setStep("code");
      toast.success(`Code sent to ${data.masked || identifier}`);
    } catch (err) {
      toast.error(err.response?.data?.detail ? formatApiErrorDetail(err.response.data.detail) : "Could not send code");
    } finally { setBusy(false); }
  };

  const submitApp = async (e) => {
    e?.preventDefault();
    if (!/^\d{4,10}$/.test(code)) { toast.error("Enter the code from your email/SMS"); return; }
    setBusy(true);
    try {
      const data = await continueWithEmailPhone({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        channel,
        identifier,
        code: code.trim(),
      });
      if (data.access_token) {
        toast.success("Welcome back", { style: { pointerEvents: "none" } });
        navigate("/", { replace: true });
      } else {
        setStep("done");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail ? formatApiErrorDetail(err.response.data.detail) : (err.message || "Verification failed"));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setCode(""); setDevCode("");
    await requestCode();
  };

  if (step === "done") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[var(--brand)]/10 via-white to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-6 sm:p-8 text-center space-y-4" data-testid="personnel-apply-pending">
          <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
            <Clock size={30} />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-[var(--ink)]">Awaiting admin approval</h1>
          <p className="text-sm text-gray-600 leading-snug">
            Thanks {form.name.split(" ")[0]} — we verified your {channel === "email" ? "email" : "phone"}
            {" "}<span className="font-medium">{masked}</span>. An MPire admin will approve your account shortly.
            You'll be notified via {channel === "email" ? "email" : "SMS"} the moment you're in.
          </p>
          <Button className="btn-brand w-full mt-2" onClick={() => navigate("/login")} data-testid="pending-back-to-login">
            Back to sign in
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-soft)]">
      <header className="bg-white border-b border-[var(--border)] px-4 py-3 flex items-center justify-between">
        <Link to="/login" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[var(--brand)]" data-testid="personnel-back-link">
          <ArrowLeft size={14} /> Back
        </Link>
        <Link to="/" className="flex items-center gap-2" data-testid="personnel-home-link">
          <img src="/brand/mpire-logo.png" alt="MPIRE" className="w-8 h-8 object-contain" />
          <span className="font-bold text-sm tracking-tight">MPire Personnel</span>
        </Link>
        <div className="w-16" />
      </header>

      <div className="max-w-md mx-auto p-4 sm:p-8">
        <div className="mb-6">
          <p className="label-eyebrow text-[var(--brand)]">Join the team</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1 text-[var(--ink)]">Continue with Email/Phone</h1>
          <p className="text-gray-600 mt-2 text-sm">
            Enter your name and contact info. Existing accounts sign in after verification; new accounts require admin approval.
          </p>
        </div>

        <Card className="p-4 sm:p-6">
          {step === "form" ? (
            <form onSubmit={requestCode} className="space-y-4" data-testid="personnel-apply-form">
              <div>
                <Label>Full name *</Label>
                <Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="personnel-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="personnel-email" />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+14155550100" data-testid="personnel-phone" />
                <p className="text-[11px] text-gray-500 mt-1">Use E.164 format for SMS delivery (e.g. +1 for US).</p>
              </div>

              <div>
                <Label className="text-xs">Send auth code via</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button type="button" onClick={() => setChannel("email")}
                    className={`flex items-center justify-center gap-2 rounded-md border-2 px-3 py-2.5 text-sm font-medium transition-all ${channel === "email" ? "border-[var(--brand)] bg-[var(--brand)]/5 text-[var(--brand)]" : "border-[var(--border)] hover:bg-gray-50"}`}
                    data-testid="channel-email">
                    <Mail size={14} /> Email
                  </button>
                  <button type="button" onClick={() => setChannel("sms")}
                    className={`flex items-center justify-center gap-2 rounded-md border-2 px-3 py-2.5 text-sm font-medium transition-all ${channel === "sms" ? "border-[var(--brand)] bg-[var(--brand)]/5 text-[var(--brand)]" : "border-[var(--border)] hover:bg-gray-50"}`}
                    data-testid="channel-sms">
                    <Phone size={14} /> SMS
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={busy} className="btn-brand w-full" data-testid="personnel-send-code">
                <Send size={14} className="mr-2" /> {busy ? "Sending…" : "Send auth code"}
              </Button>
            </form>
          ) : (
            <form onSubmit={submitApp} className="space-y-4" data-testid="personnel-code-form">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-xs">
                  <p className="font-semibold text-emerald-900">Code sent to {masked || identifier}</p>
                  <p className="text-emerald-700 mt-0.5">Enter the 6-digit code within 10 minutes.</p>
                </div>
              </div>
              {devCode && (
                <div className="rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 p-3 text-xs" data-testid="personnel-dev-code">
                  <p className="font-semibold text-amber-900">Dev mode — provider keys not set</p>
                  <p className="text-amber-800 mt-1">Your auth code: <code className="font-mono font-bold text-base">{devCode}</code></p>
                </div>
              )}
              <div>
                <Label>Auth code</Label>
                <Input required value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} inputMode="numeric" maxLength={6} placeholder="123456" data-testid="personnel-code-input" />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button type="submit" disabled={busy} className="btn-brand flex-1" data-testid="personnel-verify-submit">
                  {busy ? "Verifying…" : "Verify & submit"}
                </Button>
                <Button type="button" variant="outline" onClick={resend} disabled={busy} data-testid="personnel-resend">
                  Resend
                </Button>
              </div>
              <button type="button" onClick={() => { setStep("form"); setCode(""); setDevCode(""); }} className="text-xs text-gray-500 hover:underline w-full text-center" data-testid="personnel-change-details">
                ← Change my details
              </button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
